import {
  _ComponentDefaultFilterKeys,
  FilterInfoOperator,
  FormattingRuleOperator,
  type FormattingOperator,
  type Operator,
} from '@ardoq/api-types';

export enum NumberValueKind {
  NONE = 'none',
  SINGLE = 'single',
  PAIR = 'pair',
  COLOR_RANGE = 'colorRange',
}

type NumberComparatorDefinition = {
  comparator: FormattingOperator;
  kind: NumberValueKind;
};

/**
 * Every number comparator the conditional-formatting editor offers, in the
 * order it offers them: what it takes as its value. The editor's comparator
 * lists, its value inputs and the AI's value checks all read this list, so a
 * new number comparator is added here.
 */
const NUMBER_COMPARATOR_DEFINITIONS: NumberComparatorDefinition[] = [
  {
    comparator: FormattingRuleOperator.COLOR_RANGE,
    kind: NumberValueKind.COLOR_RANGE,
  },
  { comparator: FilterInfoOperator.EQUALS, kind: NumberValueKind.SINGLE },
  {
    comparator: FormattingRuleOperator.NOT_EQUAL,
    kind: NumberValueKind.SINGLE,
  },
  { comparator: FilterInfoOperator.GREATER_THAN, kind: NumberValueKind.SINGLE },
  {
    comparator: FormattingRuleOperator.GREATER_THAN_OR_EQUAL_TO,
    kind: NumberValueKind.SINGLE,
  },
  { comparator: FilterInfoOperator.LESS_THAN, kind: NumberValueKind.SINGLE },
  {
    comparator: FormattingRuleOperator.LESS_THAN_OR_EQUAL_TO,
    kind: NumberValueKind.SINGLE,
  },
  { comparator: FormattingRuleOperator.BETWEEN, kind: NumberValueKind.PAIR },
  {
    comparator: FormattingRuleOperator.NOT_BETWEEN,
    kind: NumberValueKind.PAIR,
  },
  { comparator: FormattingRuleOperator.IS_EMPTY, kind: NumberValueKind.NONE },
];

/**
 * What a number comparator takes as its value, or `null` for a comparator that
 * is not a number comparator.
 */
export const getNumberValueKind = (
  comparator: Operator | FormattingOperator | undefined
): NumberValueKind | null =>
  NUMBER_COMPARATOR_DEFINITIONS.find(
    definition => definition.comparator === comparator
  )?.kind ?? null;

/** Comparators that take a from–to pair of numbers instead of a single number. */
export const isNumberPairComparator = (
  comparator: Operator | FormattingOperator | undefined
): boolean => getNumberValueKind(comparator) === NumberValueKind.PAIR;

type NumberComparatorGroupsOptions = {
  cannotBeEmpty: boolean;
};

/**
 * The comparators the editor offers for a number field. The colour range is
 * offered on its own, in a group before the others. Fields that always have a
 * value, such as the version and the reference counts, cannot be empty.
 */
export const getNumberComparatorGroups = ({
  cannotBeEmpty,
}: NumberComparatorGroupsOptions): FormattingOperator[][] => {
  const offered = NUMBER_COMPARATOR_DEFINITIONS.filter(
    ({ kind }) => !cannotBeEmpty || kind !== NumberValueKind.NONE
  );
  const isColorRange = ({ kind }: NumberComparatorDefinition): boolean =>
    kind === NumberValueKind.COLOR_RANGE;

  return [
    offered.filter(isColorRange).map(({ comparator }) => comparator),
    offered
      .filter(definition => !isColorRange(definition))
      .map(({ comparator }) => comparator),
  ];
};

/** Number fields that count things, so their rule values must be whole numbers. */
export const isWholeNumberFilter = (filterId: string): boolean =>
  filterId === _ComponentDefaultFilterKeys.INCOMING_REF_COUNT ||
  filterId === _ComponentDefaultFilterKeys.OUTGOING_REF_COUNT;
